/*
    Use Case to Funnel Stage, and who may change it (MPN-1688's reference tab, MPN-2047).

    The mapping decides which rollup a campaign lands in, so it is shown next to the report
    that uses it rather than buried in Django admin -- somebody reading a rollup they do not
    believe should be able to see the rule from the same page.

    THE CODE IS NOT EDITABLE, and the column says so. Campaigns store it, so changing one would
    leave those campaigns holding a string that matches nothing, and they would move to
    Uncategorised without anybody touching them. A rewording is a `name` change; a value that
    should stop being offered is retired, not renamed.

    WHAT MPN-2047 CHANGED:

      - Two columns. What this table is and what editing it costs sit beside the table rather
        than as two muted sentences above and below it, where the one about retirement was
        under the fold.
      - The rows are grouped under the vertical they belong to. Verticals were a column, which
        read as a property of each use case rather than as the scope of the whole list.
      - Editing is behind an explicit Edit, and only for somebody who may. Every row was a live
        input and a live select: a mis-click on a select re-filed a portfolio with no
        confirmation and no undo, and the save happened on blur, so the person who did it may
        not have known they had.
      - Stopouts cannot be repointed at all, here or on the endpoint. A stopout is somebody who
        left; its stage is what the use case means, not a setting.
      - "All Campaigns", not "Campaigns". The count is portfolio-wide and was read as "campaigns
        in this report", which is a much smaller number and made retiring an option look safe.
*/
import { Check, Lock, Pencil, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
    getUseCaseMapping, updateUseCaseMapping, type UseCaseMapping,
} from "@services/analytics/holdoutReportService";

const STAGES = [
    { value: "lead_to_purchase", label: "Lead → Purchase" },
    { value: "retention", label: "Retention" },
    { value: "churn", label: "Churn" },
];

const STAGE_LABEL: Record<string, string> = Object.fromEntries(
    STAGES.map((stage) => [stage.value, stage.label]),
);

export default function UseCaseMappingTab({ canEdit }: { canEdit: boolean }) {
    const [rows, setRows] = useState<UseCaseMapping[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<number | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        getUseCaseMapping()
            .then((data) => { setRows(data); setLoaded(true); })
            .catch((e) => {
                setError(e instanceof Error ? e.message : "Could not load the mapping.");
                setLoaded(true);
            });
    }, []);

    /* Grouped by vertical, in the order the server sent them. It orders by vertical name and
       then by the sequence an admin set, and re-sorting here would throw that away. */
    const verticals = useMemo(() => {
        const groups = new Map<string, UseCaseMapping[]>();
        rows.forEach((row) => {
            const group = groups.get(row.vertical) ?? [];
            group.push(row);
            groups.set(row.vertical, group);
        });
        return [...groups];
    }, [rows]);

    const save = async (row: UseCaseMapping, patch: Partial<UseCaseMapping>) => {
        const before = rows;
        setRows(rows.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
        setSavingId(row.id);
        setError(null);
        try {
            await updateUseCaseMapping(row.id, patch as never);
            setEditingId(null);
        } catch (e) {
            // Put it back. A table still showing a change the server refused would tell the
            // next person the rollups group differently than they do.
            setRows(before);
            setError(e instanceof Error ? e.message : "Could not save that change.");
        } finally {
            setSavingId(null);
        }
    };

    if (!loaded) return <div className="holdout-report__caption">Loading the mapping...</div>;

    return (
        <div className="holdout-report__split">
            {/* The description panel. These two facts are the ones somebody needs BEFORE they
                touch a row, which is why they are beside the table rather than above and below
                it -- the retirement note used to be under the fold. */}
            <aside className="holdout-report__aside">
                <h2>Use Case to Funnel Stage</h2>
                <p>
                    Which lifecycle category each Use Case rolls up to. This mapping is what
                    the <strong>Rollup by Funnel Stage</strong> table on the Overview groups by,
                    so a change here moves every campaign set to that use case into a different
                    row of the report.
                </p>
                <p>
                    The <strong>code</strong> is what campaigns store and cannot be changed.
                    Rewording a use case is a name change; a value that should stop being
                    offered is retired rather than renamed.
                </p>
                <p>
                    Retiring one stops it being offered on new campaigns. Campaigns already set
                    to it keep rolling up where they always did.
                </p>
                {!canEdit && (
                    <p className="holdout-report__aside-note">
                        <Lock size={12} aria-hidden="true" />
                        You can see the mapping but not change it. Editing needs Platform Admin.
                    </p>
                )}
            </aside>

            <div className="holdout-report__split-main">
                {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}

                {verticals.map(([vertical, options]) => (
                    <section key={vertical} className="holdout-report__card">
                        {/* The vertical as a band over its rows rather than a repeated column.
                            A column read as a property of each use case; this reads as the
                            scope of the list, which is what it is. */}
                        <div className="holdout-report__vertical-bar">
                            <span className="holdout-report__vertical-name">
                                {vertical.toUpperCase()} VERTICAL
                            </span>
                            <span className="holdout-report__count">{options.length}</span>
                            {/* Adding one is an insert, not a migration -- but not from here
                                yet. Saying so beats leaving people to guess whether the list is
                                closed; it names who can, rather than offering a dead button. */}
                            <span className="holdout-report__vertical-hint">
                                New use cases are added by engineering. Ask rather than
                                repurposing one that is in use.
                            </span>
                        </div>

                        <div className="holdout-report__table-wrap">
                            <table className="holdout-report__table">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Name</th>
                                        <th>Funnel Stage</th>
                                        {/* "All Campaigns": the count is every campaign in the
                                            platform holding this code, not the ones in this
                                            report. Read as the latter it is a much smaller
                                            number, and retiring an option looked safe. */}
                                        <th className="is-num">All Campaigns</th>
                                        <th>Offered</th>
                                        {canEdit && <th />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {options.map((row) => (
                                        <Row key={row.id} row={row}
                                            canEdit={canEdit}
                                            editing={editingId === row.id}
                                            saving={savingId === row.id}
                                            onEdit={() => setEditingId(row.id)}
                                            onCancel={() => setEditingId(null)}
                                            onSave={(patch) => void save(row, patch)} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                ))}

                {rows.length === 0 && (
                    <div className="holdout-report__card holdout-report__empty">
                        {/* Never names a shell command: the people who read this tab are the
                            ones who cannot run one. It says what is true and who to ask. */}
                        No use cases exist yet, so every campaign reports as Uncategorised. They
                        are installed with the platform, so this being empty means something went
                        wrong on the last release. Ask engineering rather than working around it.
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ row, canEdit, editing, saving, onEdit, onCancel, onSave }: {
    row: UseCaseMapping;
    canEdit: boolean; editing: boolean; saving: boolean;
    onEdit: () => void; onCancel: () => void;
    onSave: (patch: Partial<UseCaseMapping>) => void;
}) {
    const [name, setName] = useState(row.name);
    const [stage, setStage] = useState(row.funnel_stage);

    // Reset the draft whenever the row goes back into edit, so a cancelled edit does not come
    // back the next time it is opened.
    useEffect(() => {
        if (editing) { setName(row.name); setStage(row.funnel_stage); }
    }, [editing, row.name, row.funnel_stage]);

    const dirty = name.trim() !== row.name || stage !== row.funnel_stage;

    return (
        <tr className={row.is_active ? undefined : "is-retired"}>
            <td><code>{row.code}</code></td>
            <td className="is-name">
                {editing ? (
                    <input className="form-control form-control-sm"
                        aria-label={`Name for ${row.code}`}
                        value={name} disabled={saving}
                        onChange={(e) => setName(e.target.value)} />
                ) : row.name}
            </td>
            <td>
                {editing && !row.stage_locked ? (
                    <select className="form-select form-select-sm"
                        aria-label={`Funnel stage for ${row.code}`}
                        value={stage} disabled={saving}
                        onChange={(e) => setStage(e.target.value)}>
                        {STAGES.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                ) : (
                    <span className="holdout-report__stage">
                        {STAGE_LABEL[row.funnel_stage] ?? row.funnel_stage}
                        {/* The lock, with its reason on hover. A disabled select with no
                            explanation reads as a bug; the endpoint refuses this too, so the
                            two cannot disagree. */}
                        {row.stage_locked && (
                            <span className="holdout-report__locked"
                                title={`${row.name} always rolls up here. Its funnel stage is part of what the use case means, so it is not editable.`}>
                                <Lock size={11} aria-hidden="true" />
                                Fixed
                            </span>
                        )}
                    </span>
                )}
            </td>
            <td className="is-num">
                {row.campaigns > 0
                    ? row.campaigns.toLocaleString()
                    : <span className="is-empty">0</span>}
            </td>
            <td>
                {editing ? (
                    <div className="form-check form-switch m-0">
                        <input className="form-check-input" type="checkbox"
                            aria-label={`Offer ${row.code}`}
                            checked={row.is_active} disabled={saving}
                            onChange={(e) => onSave({ is_active: e.target.checked })} />
                    </div>
                ) : (
                    <span className={`holdout-report__offered${
                        row.is_active ? " holdout-report__offered--on" : ""}`}>
                        {row.is_active ? "Offered" : "Retired"}
                    </span>
                )}
            </td>
            {canEdit && (
                <td>
                    {editing ? (
                        <span className="holdout-report__row-actions">
                            <button type="button" className="holdout-report__row-action"
                                disabled={saving || !dirty}
                                onClick={() => onSave({
                                    name: name.trim(),
                                    ...(row.stage_locked ? {} : { funnel_stage: stage }),
                                })}>
                                <Check size={12} aria-hidden="true" />
                                {saving ? "Saving..." : "Save"}
                            </button>
                            <button type="button" className="holdout-report__row-action"
                                disabled={saving} onClick={onCancel}>
                                <X size={12} aria-hidden="true" />
                                Cancel
                            </button>
                        </span>
                    ) : (
                        <button type="button" className="holdout-report__row-action"
                            onClick={onEdit}>
                            <Pencil size={12} aria-hidden="true" />
                            Edit
                        </button>
                    )}
                </td>
            )}
        </tr>
    );
}
